// components/salidas/ApoyoMisionerosModal.js
// Registro de salidas por apoyo a misioneros, desglosado por título (Misionero, Evangelista,
// Apoyo en Traducción de la Biblia...). Por cada título se captura el monto total entregado y se
// marca a quiénes se les dio, validando que la suma de sus apoyos configurados coincida con ese
// monto. Es exclusivo de Salidas Tesorería Misiones: no toca nada de Tesorería General.
import { useEffect, useMemo, useState } from "react";
import { FaExclamationTriangle, FaPlus, FaTimes } from "react-icons/fa";
import Modal from "../Modal";

const API = "/api";

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const hoyMexico = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" }).format(new Date());

const clasesInput =
  "w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none";

const APARTADOS = [
  { clave: "misiones", etiqueta: "Misiones" },
  { clave: "biblia", etiqueta: "Biblia" },
  { clave: "especial", etiqueta: "Especial" },
];

const DESTINO_OFRENDA_VACIO = { tipo: "", id_misionero: "", nombre: "", monto: "" };
const OFRENDA_AMOR_VACIA = { destinos: [{ ...DESTINO_OFRENDA_VACIO }], apartados: new Set(), montosApartado: {} };

export default function ApoyoMisionerosModal({ open, onClose, onRegistrado }) {
  const [cargando, setCargando] = useState(false);
  const [misioneros, setMisioneros] = useState([]);
  const [fecha, setFecha] = useState(hoyMexico());
  const [disponible, setDisponible] = useState({ misiones: 0, biblia: 0, especial: 0 });
  // { [titulo]: { monto: string, seleccionados: Set<id_misionero>, apartados: Set<string>, montosApartado: { [apartado]: string } } }
  const [secciones, setSecciones] = useState({});
  // Apoyo extra a una o varias personas (registradas o no), aparte de los títulos de arriba.
  const [ofrendaAmor, setOfrendaAmor] = useState(OFRENDA_AMOR_VACIA);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setFecha(hoyMexico());
    setSecciones({});
    setOfrendaAmor(OFRENDA_AMOR_VACIA);
    (async () => {
      setCargando(true);
      try {
        const [resMisioneros, resDisponible] = await Promise.all([
          fetch(`${API}/misioneros/activos`, { credentials: "include" }),
          fetch(`${API}/apoyo-misioneros/disponible`, { credentials: "include" }),
        ]);
        if (!resMisioneros.ok) throw new Error("Error obteniendo misioneros");
        setMisioneros(await resMisioneros.json());
        if (resDisponible.ok) setDisponible(await resDisponible.json());
      } catch (err) {
        console.error(err);
        setError("No se pudo cargar la lista de misioneros");
      } finally {
        setCargando(false);
      }
    })();
  }, [open]);

  // Agrupados por título; solo títulos con al menos un misionero activo aparecen.
  const grupos = useMemo(() => {
    const porTitulo = {};
    misioneros.forEach((m) => {
      const titulo = m.titulo || "Sin título";
      (porTitulo[titulo] ||= []).push(m);
    });
    return Object.entries(porTitulo).map(([titulo, lista]) => ({
      titulo,
      misioneros: lista.sort((a, b) => a.nombre.localeCompare(b.nombre)),
    }));
  }, [misioneros]);

  const seccion = (titulo) => secciones[titulo] || { monto: "", seleccionados: new Set(), apartados: new Set(), montosApartado: {} };

  const cambiarMonto = (titulo, monto) =>
    setSecciones((s) => ({ ...s, [titulo]: { ...seccion(titulo), monto } }));

  const alternarMisionero = (titulo, id) =>
    setSecciones((s) => {
      const actual = seccion(titulo);
      const seleccionados = new Set(actual.seleccionados);
      seleccionados.has(id) ? seleccionados.delete(id) : seleccionados.add(id);
      return { ...s, [titulo]: { ...actual, seleccionados } };
    });

  const alternarApartado = (titulo, clave) =>
    setSecciones((s) => {
      const actual = seccion(titulo);
      const apartados = new Set(actual.apartados);
      apartados.has(clave) ? apartados.delete(clave) : apartados.add(clave);
      return { ...s, [titulo]: { ...actual, apartados } };
    });

  const cambiarMontoApartado = (titulo, clave, valor) =>
    setSecciones((s) => {
      const actual = seccion(titulo);
      return { ...s, [titulo]: { ...actual, montosApartado: { ...actual.montosApartado, [clave]: valor } } };
    });

  const sumaSeleccionados = (lista, seleccionados) =>
    lista.filter((m) => seleccionados.has(m.id_misionero)).reduce((suma, m) => suma + Number(m.cantidad_apoyo_mensual || 0), 0);

  // Misioneros de "Ofrenda de amor": todos, sin agrupar por título (puede ser cualquiera).
  const misionerosOrdenados = useMemo(() => [...misioneros].sort((a, b) => a.nombre.localeCompare(b.nombre)), [misioneros]);

  const agregarDestinoOfrenda = () =>
    setOfrendaAmor((o) => ({ ...o, destinos: [...o.destinos, { ...DESTINO_OFRENDA_VACIO }] }));

  const quitarDestinoOfrenda = (idx) =>
    setOfrendaAmor((o) => ({
      ...o,
      destinos: o.destinos.length === 1 ? [{ ...DESTINO_OFRENDA_VACIO }] : o.destinos.filter((_, i) => i !== idx),
    }));

  const cambiarMontoDestinoOfrenda = (idx, valor) =>
    setOfrendaAmor((o) => ({ ...o, destinos: o.destinos.map((d, i) => (i === idx ? { ...d, monto: valor } : d)) }));

  const cambiarNombreDestinoOfrenda = (idx, valor) =>
    setOfrendaAmor((o) => ({ ...o, destinos: o.destinos.map((d, i) => (i === idx ? { ...d, nombre: valor } : d)) }));

  // El <select> trae los misioneros y, al final, "Persona no registrada": si se elige esa opción
  // se limpia el id y se muestra el campo de texto; si se elige un misionero, se limpia el nombre.
  const elegirEnDestinoOfrenda = (idx, valor) =>
    setOfrendaAmor((o) => ({
      ...o,
      destinos: o.destinos.map((d, i) => {
        if (i !== idx) return d;
        if (valor === "__libre__") return { ...d, tipo: "libre", id_misionero: "" };
        return { ...d, tipo: "registrado", id_misionero: valor, nombre: "" };
      }),
    }));

  const alternarApartadoOfrenda = (clave) =>
    setOfrendaAmor((o) => {
      const apartados = new Set(o.apartados);
      apartados.has(clave) ? apartados.delete(clave) : apartados.add(clave);
      return { ...o, apartados };
    });

  const cambiarMontoApartadoOfrenda = (clave, valor) =>
    setOfrendaAmor((o) => ({ ...o, montosApartado: { ...o.montosApartado, [clave]: valor } }));

  // Renglones realmente llenados: con destinatario (misionero elegido o nombre capturado) y monto.
  const destinosUtilesOfrenda = ofrendaAmor.destinos.filter(
    (d) => ((d.tipo === "registrado" && d.id_misionero) || (d.tipo === "libre" && d.nombre.trim())) && d.monto
  );
  const montoTotalOfrenda = Math.round(destinosUtilesOfrenda.reduce((s, d) => s + (Number(d.monto) || 0), 0) * 100) / 100;

  const apartadosEfectivosOfrenda = () => {
    const lista = [...ofrendaAmor.apartados];
    if (lista.length === 0) return [];
    if (lista.length === 1) return [{ apartado: lista[0], monto: montoTotalOfrenda }];
    return lista.map((clave) => ({ apartado: clave, monto: Number(ofrendaAmor.montosApartado[clave]) || 0 }));
  };

  // De dónde sale el dinero de esta sección: si solo hay un apartado marcado, sale completo de ahí
  // (no hace falta capturarlo aparte); si hay varios, se usa lo que se capturó para cada uno.
  const apartadosEfectivos = ({ monto, apartados, montosApartado }) => {
    const lista = [...apartados];
    if (lista.length === 0) return [];
    if (lista.length === 1) return [{ apartado: lista[0], monto: Number(monto) || 0 }];
    return lista.map((clave) => ({ apartado: clave, monto: Number(montosApartado[clave]) || 0 }));
  };

  // Lo solicitado en cada apartado, sumando todas las secciones en uso (para no exceder el
  // disponible real al repartir entre varios títulos a la vez).
  const solicitadoPorApartado = useMemo(() => {
    const totales = { misiones: 0, biblia: 0, especial: 0 };
    grupos.forEach(({ titulo }) => {
      apartadosEfectivos(seccion(titulo)).forEach(({ apartado, monto }) => { totales[apartado] += monto; });
    });
    apartadosEfectivosOfrenda().forEach(({ apartado, monto }) => { totales[apartado] += monto; });
    return totales;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupos, secciones, ofrendaAmor]);

  const excedeAlgunApartado = APARTADOS.some(({ clave }) => solicitadoPorApartado[clave] - Number(disponible[clave] || 0) > 0.009);

  const guardar = async () => {
    const cargaUtil = [];
    for (const { titulo, misioneros: lista } of grupos) {
      const { monto, seleccionados, apartados } = seccion(titulo);
      if (!monto && seleccionados.size === 0) continue; // sección sin usar, se omite

      const montoNum = Number(monto);
      if (!(montoNum > 0)) return setError(`Indica el monto entregado en "${titulo}"`);
      if (seleccionados.size === 0) return setError(`Selecciona al menos un misionero en "${titulo}"`);

      const suma = sumaSeleccionados(lista, seleccionados);
      if (Math.abs(suma - montoNum) > 0.009) {
        return setError(`En "${titulo}" el monto (${formatoMoneda(montoNum)}) no coincide con la suma de los seleccionados (${formatoMoneda(suma)})`);
      }

      if (apartados.size === 0) return setError(`Indica de dónde sale el dinero de "${titulo}" (Misiones, Biblia o Especial)`);
      const apartadosEnv = apartadosEfectivos(seccion(titulo));
      const sumaApartados = apartadosEnv.reduce((s, a) => s + a.monto, 0);
      if (Math.abs(sumaApartados - montoNum) > 0.009) {
        return setError(`En "${titulo}" lo capturado por apartado (${formatoMoneda(sumaApartados)}) no coincide con el monto (${formatoMoneda(montoNum)})`);
      }

      cargaUtil.push({ monto_total: montoNum, ids_misioneros: [...seleccionados], apartados: apartadosEnv });
    }

    let ofrendaAmorEnv = null;
    if (destinosUtilesOfrenda.length > 0) {
      for (const d of destinosUtilesOfrenda) {
        if (!(Number(d.monto) > 0)) return setError('Indica el monto de cada persona en "Ofrenda de amor"');
      }
      if (ofrendaAmor.apartados.size === 0) return setError('Indica de dónde sale el dinero de "Ofrenda de amor" (Misiones, Biblia o Especial)');
      const apartadosOfrendaEnv = apartadosEfectivosOfrenda();
      const sumaApartadosOfrenda = apartadosOfrendaEnv.reduce((s, a) => s + a.monto, 0);
      if (Math.abs(sumaApartadosOfrenda - montoTotalOfrenda) > 0.009) {
        return setError(`En "Ofrenda de amor" lo capturado por apartado (${formatoMoneda(sumaApartadosOfrenda)}) no coincide con el monto (${formatoMoneda(montoTotalOfrenda)})`);
      }
      ofrendaAmorEnv = {
        destinos: destinosUtilesOfrenda.map((d) =>
          d.tipo === "registrado" ? { id_misionero: Number(d.id_misionero), monto: Number(d.monto) } : { nombre: d.nombre.trim(), monto: Number(d.monto) }
        ),
        apartados: apartadosOfrendaEnv,
      };
    }

    if (cargaUtil.length === 0 && !ofrendaAmorEnv) return setError('Captura al menos una sección o una "Ofrenda de amor"');
    if (!fecha) return setError("Indica la fecha");
    if (excedeAlgunApartado) return setError("Lo solicitado excede el disponible de algún apartado");

    setGuardando(true);
    setError("");
    try {
      const res = await fetch(`${API}/apoyo-misioneros`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fecha, secciones: cargaUtil, ofrendaAmor: ofrendaAmorEnv }),
      });
      const cuerpo = await res.json().catch(() => ({}));
      if (!res.ok) return setError(cuerpo.mensaje || "Error registrando el apoyo");
      onRegistrado?.();
      onClose();
    } catch (err) {
      console.error(err);
      setError("Error inesperado al guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Registrar apoyo a misioneros" maxWidth="max-w-2xl">
      {cargando ? (
        <p className="text-center text-slate-400 py-10">Cargando...</p>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Fecha <span className="text-rose-500">*</span>
              </label>
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={`${clasesInput} max-w-[200px]`} />
            </div>
            <div className="flex flex-wrap gap-2">
              {APARTADOS.map(({ clave, etiqueta }) => (
                <div key={clave} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-right">
                  <p className="text-slate-400">{etiqueta} disponible</p>
                  <p className="font-semibold text-slate-700 tabular-nums">{formatoMoneda(disponible[clave])}</p>
                </div>
              ))}
            </div>
          </div>

          {grupos.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded-lg">
              No hay misioneros activos registrados. Puedes usar "Ofrenda de amor" para registrar un apoyo de todos modos.
            </p>
          ) : (
            grupos.map(({ titulo, misioneros: lista }) => {
              const sec = seccion(titulo);
              const { monto, seleccionados, apartados, montosApartado } = sec;
              const suma = sumaSeleccionados(lista, seleccionados);
              const montoNum = Number(monto);
              const coincide = monto !== "" && Math.abs(suma - montoNum) <= 0.009;
              const hayDesajuste = monto !== "" && seleccionados.size > 0 && !coincide;
              const sumaApartados = apartadosEfectivos(sec).reduce((s, a) => s + a.monto, 0);
              const apartadosCoinciden = apartados.size > 0 && monto !== "" && Math.abs(sumaApartados - montoNum) <= 0.009;

              return (
                <section key={titulo} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">{titulo}</h3>
                    <div className="w-36">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={monto}
                          onChange={(e) => cambiarMonto(titulo, e.target.value)}
                          placeholder="0.00"
                          className={`${clasesInput} pl-6`}
                        />
                      </div>
                    </div>
                  </div>

                  <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                    {lista.map((m) => {
                      const sinMonto = m.cantidad_apoyo_mensual === null || m.cantidad_apoyo_mensual === undefined;
                      return (
                        <li key={m.id_misionero} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={seleccionados.has(m.id_misionero)}
                            disabled={sinMonto}
                            onChange={() => alternarMisionero(titulo, m.id_misionero)}
                            aria-label={`Entregar a ${m.nombre}`}
                            className="w-4 h-4 accent-indigo-600 disabled:cursor-not-allowed"
                          />
                          <span className="flex-1 truncate text-slate-700">{m.nombre}</span>
                          <span className="text-slate-500 tabular-nums">
                            {sinMonto ? <span className="text-amber-600">sin monto configurado</span> : formatoMoneda(m.cantidad_apoyo_mensual)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <p className={`text-xs mt-1.5 ${hayDesajuste ? "text-rose-600" : seleccionados.size > 0 ? "text-emerald-600" : "text-slate-400"}`}>
                    Seleccionado: {formatoMoneda(suma)}{monto !== "" ? ` de ${formatoMoneda(montoNum)}` : ""}
                    {hayDesajuste && " · no coincide"}
                  </p>

                  <div className="mt-2.5 pt-2.5 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-700 mb-1.5">¿De dónde sale el dinero?</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {APARTADOS.map(({ clave, etiqueta }) => (
                        <label key={clave} className="flex items-center gap-1.5 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={apartados.has(clave)}
                            onChange={() => alternarApartado(titulo, clave)}
                            className="w-4 h-4 accent-indigo-600"
                          />
                          {etiqueta}
                          {apartados.size > 1 && apartados.has(clave) && (
                            <span className="relative ml-1">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={montosApartado[clave] || ""}
                                onChange={(e) => cambiarMontoApartado(titulo, clave, e.target.value)}
                                placeholder="0.00"
                                className="w-24 border border-slate-200 rounded-lg pl-5 pr-2 py-1 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                              />
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                    {apartados.size > 0 && monto !== "" && (
                      <p className={`text-xs mt-1.5 ${apartadosCoinciden ? "text-emerald-600" : "text-rose-600"}`}>
                        Capturado por apartado: {formatoMoneda(sumaApartados)} de {formatoMoneda(montoNum)}
                      </p>
                    )}
                  </div>
                </section>
              );
            })
          )}

          <section className="border border-slate-200 rounded-lg p-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="text-sm font-semibold text-slate-800">Ofrenda de amor</h3>
              <span className="text-xs text-slate-400">Apoyo extra, no el mensual configurado</span>
            </div>

            <div className="space-y-2">
              {ofrendaAmor.destinos.map((d, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2">
                  <select
                    value={d.tipo === "libre" ? "__libre__" : d.id_misionero}
                    onChange={(e) => elegirEnDestinoOfrenda(idx, e.target.value)}
                    className={`${clasesInput} flex-1 min-w-[180px]`}
                  >
                    <option value="">-- Selecciona un misionero --</option>
                    {misionerosOrdenados.map((m) => (
                      <option key={m.id_misionero} value={m.id_misionero}>{m.nombre}</option>
                    ))}
                    <option value="__libre__">Persona no registrada...</option>
                  </select>
                  {d.tipo === "libre" && (
                    <input
                      type="text"
                      value={d.nombre}
                      onChange={(e) => cambiarNombreDestinoOfrenda(idx, e.target.value)}
                      placeholder="Nombre completo"
                      maxLength={150}
                      className={`${clasesInput} flex-1 min-w-[150px]`}
                    />
                  )}
                  <div className="relative w-28 shrink-0">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={d.monto}
                      onChange={(e) => cambiarMontoDestinoOfrenda(idx, e.target.value)}
                      placeholder="0.00"
                      className={`${clasesInput} pl-6`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => quitarDestinoOfrenda(idx)}
                    title="Quitar"
                    className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={agregarDestinoOfrenda}
              className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
            >
              <FaPlus className="text-[10px]" /> Agregar persona
            </button>

            {destinosUtilesOfrenda.length > 0 && (
              <div className="mt-2.5 pt-2.5 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-700 mb-1.5">¿De dónde sale el dinero?</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {APARTADOS.map(({ clave, etiqueta }) => (
                    <label key={clave} className="flex items-center gap-1.5 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={ofrendaAmor.apartados.has(clave)}
                        onChange={() => alternarApartadoOfrenda(clave)}
                        className="w-4 h-4 accent-indigo-600"
                      />
                      {etiqueta}
                      {ofrendaAmor.apartados.size > 1 && ofrendaAmor.apartados.has(clave) && (
                        <span className="relative ml-1">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={ofrendaAmor.montosApartado[clave] || ""}
                            onChange={(e) => cambiarMontoApartadoOfrenda(clave, e.target.value)}
                            placeholder="0.00"
                            className="w-24 border border-slate-200 rounded-lg pl-5 pr-2 py-1 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                          />
                        </span>
                      )}
                    </label>
                  ))}
                </div>
                {ofrendaAmor.apartados.size > 0 && (
                  <p className={`text-xs mt-1.5 ${Math.abs(apartadosEfectivosOfrenda().reduce((s, a) => s + a.monto, 0) - montoTotalOfrenda) <= 0.009 ? "text-emerald-600" : "text-rose-600"}`}>
                    Capturado por apartado: {formatoMoneda(apartadosEfectivosOfrenda().reduce((s, a) => s + a.monto, 0))} de {formatoMoneda(montoTotalOfrenda)}
                  </p>
                )}
              </div>
            )}
          </section>

          {excedeAlgunApartado && (
            <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <FaExclamationTriangle className="shrink-0 mt-0.5 text-xs" /> Lo que estás repartiendo excede el disponible de algún apartado.
            </p>
          )}

          {error && (
            <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <FaExclamationTriangle className="shrink-0 mt-0.5 text-xs" /> {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button onClick={onClose} className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
              Cancelar
            </button>
            <button
              onClick={guardar}
              disabled={guardando || (grupos.length === 0 && destinosUtilesOfrenda.length === 0) || excedeAlgunApartado}
              className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition disabled:opacity-50"
            >
              {guardando ? "Guardando..." : "Registrar"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
